import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { LegalPage, P, UL } from "@/components/marketing/LegalPage";

export const Route = createFileRoute("/legal/aml-global")({
  head: () => ({
    meta: [
      { title: "AML, Sanctions & Financial Crime (Global) — banana.cards" },
      { name: "description", content: "Anti-Money Laundering, counter-terrorist financing, sanctions, and financial crime compliance terms for Organisers using Banana Cards globally." },
      { property: "og:title", content: "AML, Sanctions & Financial Crime (Global) — banana.cards" },
      { property: "og:description", content: "AML/CFT, sanctions, and financial crime compliance terms for global Organisers." },
    ],
    links: alternateLinkList("/legal/aml-global", "en"),
  }),
  component: AmlGlobalPage,
});

export function AmlGlobalPage() {
  return (
    <LegalPage
      eyebrow="Legal · Compliance"
      title="AML, Sanctions &"
      italic="Financial Crime."
      updated="January 2026"
      intro="The Organiser agrees that Banana Cards and Six Studio Creative FZCO may carry out checks, monitoring, reviews, and controls required or considered appropriate for anti-money laundering, counter-terrorist financing, sanctions, fraud prevention, anti-bribery, anti-corruption, tax compliance, payment risk, and financial crime compliance purposes. These controls may be applied before an Organiser account is approved, before an event is published, before ticket sales are enabled, before payouts are released, during an event campaign, or after an event has ended."
      related={[
        { label: "AML — Vietnam", to: "/legal/aml-vietnam" },
        { label: "KYC — Global", to: "/legal/kyc-global" },
        { label: "Ticketing Terms (Global)", to: "/legal/ticketing-global" },
      ]}
      sections={[
        {
          id: "compliance-laws",
          title: "1. Compliance with Applicable Financial Crime Laws",
          body: (
            <>
              <P>The Organiser must comply with all applicable laws, regulations, rules, and payment-provider requirements relating to:</P>
              <UL items={[
                "anti-money laundering",
                "counter-terrorist financing",
                "counter-proliferation financing",
                "sanctions and targeted financial sanctions",
                "fraud prevention",
                "bribery and corruption",
                "tax evasion and unlawful tax avoidance",
                "illegal source-of-funds activity",
                "suspicious transaction reporting",
                "financial crime prevention",
              ]} />
              <P>Where applicable, this includes UAE anti-money laundering and counter-terrorist financing laws and regulations, UAE targeted financial sanctions requirements, United Nations sanctions measures, payment gateway rules, card network rules, and any equivalent requirements in the Organiser's country, the event location, and the countries from which Customers or Exhibitors make payments.</P>
            </>
          ),
        },
        {
          id: "representations",
          title: "2. Organiser Representations",
          body: (
            <>
              <P>The Organiser represents and warrants that:</P>
              <UL items={[
                "it is not using Banana Cards to launder money, conceal the source of funds, finance terrorism, finance illegal organisations, finance proliferation, evade sanctions, facilitate fraud, or support illegal activity",
                "it is not subject to sanctions, asset freezes, travel bans, terrorist designations, or other financial restrictions under UAE, United Nations, United States, United Kingdom, European Union, or other applicable sanctions regimes",
                "its directors, shareholders, beneficial owners, controllers, authorised representatives, event partners, sponsors, Exhibitors, and key counterparties are not sanctioned persons or entities, unless disclosed to and approved by Banana Cards in writing",
                "all funds received through Banana Cards arise from lawful event-related activity",
                "ticket revenue, exhibitor revenue, sponsorship revenue, booth revenue, and other event-related revenue are not proceeds of crime",
                "it will not structure payments, refunds, transfers, payouts, or transactions to avoid compliance checks, payment thresholds, reporting requirements, tax obligations, or legal controls",
                "it will provide accurate, complete, and up-to-date information requested by Banana Cards for compliance, verification, tax, payment, payout, and risk-management purposes",
              ]} />
            </>
          ),
        },
        {
          id: "cdd",
          title: "3. Customer Due Diligence and Verification",
          body: (
            <>
              <P>Banana Cards may require the Organiser to provide information and documents for customer due diligence, know-your-customer, know-your-business, beneficial ownership, tax, sanctions, and payout verification purposes. This may include:</P>
              <UL items={[
                "legal name and trading name",
                "company registration documents",
                "trade licence or business licence",
                "registered address and operating address",
                "tax registration details",
                "VAT/GST/TRN details, where applicable",
                "identity documents of directors, shareholders, beneficial owners, authorised representatives, or account administrators",
                "ownership and control structure",
                "bank account details and proof of account ownership",
                "source of funds and source of wealth information, where relevant",
                "event description, venue details, sponsor information, exhibitor information, and commercial purpose",
                "contracts, invoices, payment records, or supporting documents relating to ticket sales, exhibitor sales, booth sales, sponsorships, or other event payments",
              ]} />
              <P>Banana Cards may refuse onboarding, delay approval, restrict event publishing, suspend payment collection, or delay payouts if the requested information is not provided, appears incomplete, inconsistent, misleading, expired, unverifiable, or high-risk.</P>
            </>
          ),
        },
        {
          id: "monitoring",
          title: "4. Ongoing Monitoring",
          body: (
            <>
              <P>Banana Cards may monitor Organiser accounts, events, ticket sales, exhibitor payments, refunds, chargebacks, payout requests, transaction patterns, and platform activity to detect suspicious, unusual, high-risk, fraudulent, or prohibited activity. Examples of activity that may trigger review include:</P>
              <UL items={[
                "unusually high ticket sales compared with the event type or venue capacity",
                "unusually high refund volumes",
                "repeated cancelled events after payment collection",
                "high chargeback rates",
                "payments from high-risk jurisdictions",
                "payments involving sanctioned or restricted persons",
                "multiple payments from unrelated cards followed by payout requests",
                "mismatches between event description and actual activity",
                "inflated exhibitor payments or sponsorship payments without commercial justification",
                "requests to pay out to a bank account not held by the Organiser",
                "sudden changes to bank account details shortly before payout",
                "suspicious split payments, circular payments, or structured transactions",
                "refusal to provide supporting documents",
                "any activity that Banana Cards reasonably considers inconsistent with normal event ticketing activity",
              ]} />
            </>
          ),
        },
        {
          id: "sanctions",
          title: "5. Sanctions Screening",
          body: (
            <>
              <P>Banana Cards may screen the Organiser, its beneficial owners, directors, authorised representatives, Customers, Exhibitors, sponsors, payees, payment counterparties, and relevant event parties against applicable sanctions, watchlists, restricted-party lists, politically exposed person databases, adverse media databases, and other compliance sources.</P>
              <P>Banana Cards may block, reject, freeze, delay, reverse, or withhold a transaction, ticket sale, exhibitor payment, refund, account, payout, or platform access where required or reasonably considered necessary for sanctions, AML/CFT, fraud, payment, legal, or risk-management purposes.</P>
              <P>The Organiser must not use Banana Cards to make available funds, economic resources, ticket access, booth access, event benefits, sponsorship benefits, or services to any person or entity where doing so would breach applicable sanctions or financial crime laws.</P>
            </>
          ),
        },
        {
          id: "payout-controls",
          title: "6. Payout Controls",
          body: (
            <>
              <P>Banana Cards may delay, withhold, suspend, or refuse payout where:</P>
              <UL items={[
                "Organiser verification has not been completed",
                "beneficial ownership information has not been provided",
                "bank account ownership cannot be verified",
                "transaction activity appears suspicious, unusual, fraudulent, or high-risk",
                "a payment provider, bank, regulator, court, authority, or law enforcement body requests or requires a hold",
                "a sanctions, AML/CFT, fraud, chargeback, tax, or legal review is ongoing",
                "the Organiser, event, Customer, Exhibitor, sponsor, or payee appears on a sanctions list, watchlist, or restricted-party list",
                "Banana Cards reasonably believes that releasing funds may breach law, payment rules, sanctions obligations, or financial crime controls",
              ]} />
              <P>Banana Cards is not liable for any delay, withholding, suspension, reversal, or refusal of payout where such action is taken in good faith for compliance, legal, payment, fraud-prevention, sanctions, AML/CFT, or risk-management reasons.</P>
            </>
          ),
        },
        {
          id: "suspicious-reporting",
          title: "7. Suspicious Activity and Reporting",
          body: (
            <>
              <P>Banana Cards may report suspicious, unusual, or prohibited activity to competent authorities, regulators, financial intelligence units, payment providers, banks, card networks, or law enforcement bodies where required or permitted by law.</P>
              <P>Banana Cards may do so without notifying the Organiser where notification is prohibited by law, could interfere with an investigation, could create tipping-off risk, or could increase legal, regulatory, payment, or financial crime risk.</P>
            </>
          ),
        },
        {
          id: "prohibited",
          title: "8. Prohibited Use",
          body: (
            <>
              <P>The Organiser must not use Banana Cards for:</P>
              <UL items={[
                "sham events, fake events, or events created primarily to move funds",
                "transactions without genuine commercial purpose",
                "laundering criminal proceeds",
                "terrorist financing or financing illegal organisations",
                "proliferation financing",
                "sanctions evasion",
                "bribery, corruption, or illegal political financing",
                "illegal gambling, betting, lottery, or gaming activity",
                "illegal investment schemes, pyramid schemes, or fraudulent fundraising",
                "sale of prohibited goods or services",
                "refund abuse, chargeback abuse, or payment card testing",
                "artificially inflating ticket sales, exhibitor fees, sponsorships, or booth payments to justify payouts",
                "receiving funds for a third party without disclosure and approval",
                "disguising the true payer, payee, source of funds, beneficial owner, event purpose, or transaction purpose",
              ]} />
            </>
          ),
        },
        {
          id: "cooperation",
          title: "9. Cooperation Obligation",
          body: (
            <>
              <P>The Organiser must promptly cooperate with Banana Cards in relation to compliance reviews, payment investigations, chargeback investigations, sanctions reviews, AML/CFT reviews, fraud checks, tax reviews, or law enforcement requests.</P>
              <P>The Organiser must provide requested information and supporting documents within the timeframe specified by Banana Cards. Failure to cooperate may result in event suspension, account restriction, payout delay, payout refusal, termination, reporting to authorities, or any other action permitted under these Terms.</P>
            </>
          ),
        },
        {
          id: "suspend-terminate",
          title: "10. Right to Suspend or Terminate",
          body: (
            <>
              <P>Banana Cards may suspend, restrict, interrupt, or terminate the Organiser's account, event, payment collection, ticketing function, exhibitor management function, payout release, invoice coordination, access control, check-in support, or any related platform service if Banana Cards reasonably believes that:</P>
              <UL items={[
                "the Organiser has breached this AML, sanctions, or financial crime section",
                "the Organiser has provided false, incomplete, misleading, or unverifiable information",
                "the event or transaction presents unacceptable AML/CFT, sanctions, fraud, chargeback, payment, tax, legal, or reputational risk",
                "continued service may breach applicable law, payment rules, sanctions obligations, or financial crime controls",
                "Banana Cards is required or requested to do so by a payment provider, bank, regulator, authority, court, or law enforcement body",
              ]} />
            </>
          ),
        },
        {
          id: "no-circumvention",
          title: "11. No Circumvention",
          body: (
            <>
              <P>The Organiser must not attempt to bypass Banana Cards' compliance controls, payout controls, verification requirements, payment rules, transaction monitoring, sanctions screening, or risk review processes.</P>
              <P>Any attempt to use alternative accounts, false identities, nominee entities, undisclosed third-party bank accounts, artificial event structures, split transactions, repeated cancellations, or misleading descriptions to avoid compliance controls will be treated as a material breach of these Terms.</P>
            </>
          ),
        },
        {
          id: "survival",
          title: "12. Survival",
          body: (
            <P>The obligations in this section survive event completion, account closure, payout completion, suspension, or termination of the Organiser's access to Banana Cards.</P>
          ),
        },
      ]}
    />
  );
}